"use client";

import React, { useEffect, useState } from "react";
import {
  getDatabase,
  ref,
  onValue,
  update,
  remove,
} from "firebase/database";
import {
  FaTrash,
  FaBell,
  FaClock,
  FaExclamationTriangle,
  FaCheckSquare,
  FaSquare,
} from "react-icons/fa";
import { useAccount } from "../context/AccountContext";
import "./AltAlarm.css";
import { Trash2 } from "lucide-react";

const AltAlarmModal = ({ onClose, currentUid, currentAccount }) => {
  const [alarms, setAlarms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedAlarms, setSelectedAlarms] = useState(new Set());
  const [showSingleDeleteModal, setShowSingleDeleteModal] = useState(false);
  const [selectedAlarm, setSelectedAlarm] = useState(null);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);

  // Fetch from new "alerts" node
  useEffect(() => {
    if (!currentAccount?.uid) {
      setAlarms([]);
      setLoading(false);
      return;
    }

    const db = getDatabase();
    const alarmsRef = ref(db, `alerts/${currentAccount.uid}`);

    const unsubscribe = onValue(alarmsRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const list = Object.entries(data)
          .map(([alarmId, alarm]) => ({
            alarmId,
            ...alarm,
          }))
          .sort((a, b) =>
            new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime()
          );

        setAlarms(list);
      } else {
        setAlarms([]);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [currentAccount]);

  const clearNewAlarms = async () => {
    if (!currentAccount?.uid) return;
    const db = getDatabase();
    const updates = {};
    alarms.forEach((alarm) => {
      updates[`${alarm.alarmId}/isNew`] = false;
    });
    await update(ref(db, `alerts/${currentAccount.uid}`), updates);
  };

  const toggleSelectionMode = () => {
    setIsSelectionMode(!isSelectionMode);
    setSelectedAlarms(new Set());
  };

  const toggleAlarmSelection = (alarmId) => {
    const newSelected = new Set(selectedAlarms);
    if (newSelected.has(alarmId)) newSelected.delete(alarmId);
    else newSelected.add(alarmId);
    setSelectedAlarms(newSelected);
  };

  const selectAll = () => {
    if (selectedAlarms.size === alarms.length) {
      setSelectedAlarms(new Set());
    } else {
      setSelectedAlarms(new Set(alarms.map((a) => a.alarmId)));
    }
  };

  const openSingleDeleteModal = (alarm) => {
    setSelectedAlarm(alarm);
    setShowSingleDeleteModal(true);
  };

  const confirmSingleDelete = async () => {
    if (!selectedAlarm || !currentAccount?.uid) return;
    try {
      const db = getDatabase();
      await remove(ref(db, `alerts/${currentAccount.uid}/${selectedAlarm.alarmId}`));
      setAlarms((prev) => prev.filter((item) => item.alarmId !== selectedAlarm.alarmId));
      setShowSingleDeleteModal(false);
    } catch (err) {
      console.error(err);
    }
  };

  const confirmBulkDelete = async () => {
    if (selectedAlarms.size === 0 || !currentAccount?.uid) return;
    try {
      const db = getDatabase();
      const updates = {};
      selectedAlarms.forEach((alarmId) => (updates[`${alarmId}`] = null));
      await update(ref(db, `alerts/${currentAccount.uid}`), updates);
      setAlarms((prev) => prev.filter((alarm) => !selectedAlarms.has(alarm.alarmId)));
      setSelectedAlarms(new Set());
      setIsSelectionMode(false);
      setShowBulkDeleteModal(false);
    } catch (err) {
      console.error(err);
    }
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      timeZone: "Asia/Kolkata",
    });
  };

  if (loading) return <div className="alarm-loading">Loading alerts...</div>;

  if (alarms.length === 0) {
    return (
      <div className="alarm-empty-modal">
        <FaBell size={30} />
        <h5>No Alerts</h5>
        <p>You're all caught up.</p>
        <button onClick={onClose} className="close-btn">Close</button>
      </div>
    );
  }

  return (
    <div className="alarm-dropdown-content">
      {/* Header */}
      <div className="alarm-header">
        <h3>Alerts ({alarms.length})</h3>
        <button onClick={onClose} className="close-modal-btn">✕</button>
      </div>

      {alarms.length > 0 && (
        <button className="clear-alert-btn" onClick={clearNewAlarms}>
          Mark All Read
        </button>
      )}

      {/* Selection Controls */}
      <div className="alarm-selection-bar">
        <button className="select-mode-btn" onClick={toggleSelectionMode}>

          {isSelectionMode ? "Cancel Selection" : ""} {" "}
          <Trash2 size={16} />
        </button>
        {isSelectionMode && (
          <>
            <button className="select-all-btn" onClick={selectAll}>
              {selectedAlarms.size === alarms.length ? "Deselect All" : "Delete All"}
            </button>
            {selectedAlarms.size > 0 && (
              <button className="bulk-delete-btn" onClick={() => setShowBulkDeleteModal(true)}>
                Delete Selected ({selectedAlarms.size})
              </button>
            )}
          </>
        )}
      </div>

      {/* Alarm List */}
      <div className="alarm-list">
        {alarms.map((alarm) => (
          <div
            key={alarm.alarmId}
            className={`alarm-card ${alarm.isNew ? "new-card" : ""} ${isSelectionMode ? "selection-mode" : ""}`}
          >
            {isSelectionMode && (
              <button
                className="checkbox-btn"
                onClick={() => toggleAlarmSelection(alarm.alarmId)}
              >
                {selectedAlarms.has(alarm.alarmId) ? (
                  <FaCheckSquare size={20} className="checkbox-selected" />
                ) : (
                  <FaSquare size={20} className="checkbox-unselected" />
                )}
              </button>
            )}

            <div className="alarm-row">
              <div className="alarm-icon danger">
                <FaExclamationTriangle />
              </div>

              <div className="alarm-content">
                <div className="alarm-title">{alarm.type || "General Alert"}</div>

                <div className="alarm-reason">
                  <strong>Reason:</strong> <span>{alarm.reason || "-"}</span>
                </div>
                {/* {alarm.company_name && (
                  <div className="alarm-company">
                    <strong>Company:</strong> <span>{alarm.company_name}</span>
                  </div>
                )} */}
              </div>

              {isSelectionMode && (
                <button
                  className="delete-single-btn"
                  onClick={() => openSingleDeleteModal(alarm)}
                >
                  <FaTrash />
                </button>
              )}
            </div>

            <div className="alarm-footer">
              <FaClock />
              <span>{formatDate(alarm.lastUpdated)}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Delete Confirmation Modals */}
      {showSingleDeleteModal && (
        <div className="alarm-modal-overlay" onClick={() => setShowSingleDeleteModal(false)}>
          <div className="alarm-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Delete Alert?</h3>
            <p>This action cannot be undone.</p>
            <div className="alarm-modal-buttons">
              <button className="cancel-btn" onClick={() => setShowSingleDeleteModal(false)}>Cancel</button>
              <button className="delete-btn" onClick={confirmSingleDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {showBulkDeleteModal && (
        <div className="alarm-modal-overlay" onClick={() => setShowBulkDeleteModal(false)}>
          <div className="alarm-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Delete {selectedAlarms.size} Alerts?</h3>
            <p>This action cannot be undone.</p>
            <div className="alarm-modal-buttons">
              <button className="cancel-btn" onClick={() => setShowBulkDeleteModal(false)}>Cancel</button>
              <button className="delete-btn" onClick={confirmBulkDelete}>Delete All</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AltAlarmModal;